import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, X, Upload, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface ProjectImage {
  id: string;
  image_url: string;
  alt_text: string;
  caption: string;
  sort_order: number;
}

interface Project {
  id: string;
  title: string;
  description: string;
  location: string;
  status: 'draft' | 'published';
  featured: boolean;
  project_images: ProjectImage[];
  project_category_assignments: { 
    project_categories: { id: string; name: string } 
  }[];
}

interface EditProjectFormProps {
  project: Project;
  onSuccess: () => void;
}

export const EditProjectForm: React.FC<EditProjectFormProps> = ({ project, onSuccess }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [replacingImageId, setReplacingImageId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: project.title,
    description: project.description || '',
    location: project.location || '',
    status: project.status,
    featured: project.featured
  });

  useEffect(() => {
    loadCategories();
    setImages(project.project_images?.sort((a, b) => a.sort_order - b.sort_order) || []);
    setSelectedCategories(
      project.project_category_assignments?.map(a => a.project_categories.id) || []
    );
  }, [project]);

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('project_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleNewImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewImages(prev => [...prev, ...files.filter(f => f.type.startsWith('image/'))]);
  };

  const removeNewImage = (index: number) => {
    setNewImages(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const removeExistingImage = async (imageId: string) => {
    try {
      // Get the image to delete from storage
      const imageToDelete = images.find(img => img.id === imageId);
      
      const { error } = await supabase
        .from('project_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;

      // Delete from storage if it's a Supabase URL
      if (imageToDelete?.image_url.includes('project-images')) {
        await deleteImageFromStorage(imageToDelete.image_url);
      }

      setImages(prev => prev.filter(img => img.id !== imageId));
      
      toast({
        title: "Success",
        description: "Image removed successfully",
      });
    } catch (error) {
      console.error('Error removing image:', error);
      toast({
        title: "Error",
        description: "Failed to remove image",
        variant: "destructive",
      });
    }
  };

  const updateImageData = (imageId: string, field: 'alt_text' | 'caption', value: string) => {
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, [field]: value } : img
    ));
  };

  const moveImageUp = (index: number) => {
    if (index > 0) {
      setImages(prev => {
        const newImages = [...prev];
        [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
        return newImages.map((img, i) => ({ ...img, sort_order: i }));
      });
    }
  };

  const moveImageDown = (index: number) => {
    setImages(prev => {
      if (index < prev.length - 1) {
        const newImages = [...prev];
        [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
        return newImages.map((img, i) => ({ ...img, sort_order: i }));
      }
      return prev;
    });
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `projects/${fileName}`;

    const { error } = await supabase.storage
      .from('project-images')
      .upload(filePath, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from('project-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const deleteImageFromStorage = async (imageUrl: string) => {
    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/project-images/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1];
        await supabase.storage
          .from('project-images')
          .remove([filePath]);
      }
    } catch (error) {
      console.error('Error deleting image from storage:', error);
    }
  };

  const handleReplaceImage = async (imageId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (!file) return;

      setReplacingImageId(imageId);
      
      try {
        // Get the current image to delete from storage
        const currentImage = images.find(img => img.id === imageId);
        
        // Upload new image
        const newImageUrl = await uploadImage(file);
        
        // Update database
        const { error } = await supabase
          .from('project_images')
          .update({ 
            image_url: newImageUrl,
            alt_text: currentImage?.alt_text || formData.title,
            caption: currentImage?.caption || ''
          })
          .eq('id', imageId);

        if (error) throw error;

        // Delete old image from storage if it's a Supabase URL
        if (currentImage?.image_url.includes('project-images')) {
          await deleteImageFromStorage(currentImage.image_url);
        }

        // Update local state
        setImages(prev => prev.map(img => 
          img.id === imageId 
            ? { ...img, image_url: newImageUrl }
            : img
        ));
        
        toast({
          title: "Success",
          description: "Image replaced successfully",
        });
      } catch (error) {
        console.error('Error replacing image:', error);
        toast({
          title: "Error",
          description: "Failed to replace image",
          variant: "destructive",
        });
      } finally {
        setReplacingImageId(null);
      }
    };
    
    input.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update project
      const { error: projectError } = await supabase
        .from('projects')
        .update({
          title: formData.title,
          description: formData.description,
          location: formData.location,
          status: formData.status,
          featured: formData.featured
        })
        .eq('id', project.id);

      if (projectError) throw projectError;

      // Update existing images (including sort order)
      for (const image of images) {
        await supabase
          .from('project_images')
          .update({
            alt_text: image.alt_text,
            caption: image.caption,
            sort_order: image.sort_order
          })
          .eq('id', image.id);
      }

      // Upload new images
      if (newImages.length > 0) {
        const imagePromises = newImages.map(async (file, index) => {
          const imageUrl = await uploadImage(file);
          
          return supabase
            .from('project_images')
            .insert({
              project_id: project.id,
              image_url: imageUrl,
              alt_text: formData.title,
              caption: '',
              sort_order: images.length + index
            });
        });

        await Promise.all(imagePromises);
      }

      // Update categories
      await supabase
        .from('project_category_assignments')
        .delete()
        .eq('project_id', project.id);

      if (selectedCategories.length > 0) {
        const categoryAssignments = selectedCategories.map(categoryId => ({
          project_id: project.id,
          category_id: categoryId
        }));

        await supabase
          .from('project_category_assignments')
          .insert(categoryAssignments);
      }

      toast({
        title: "Success",
        description: "Project updated successfully",
      });

      onSuccess();

    } catch (error) {
      console.error('Error updating project:', error);
      toast({
        title: "Error",
        description: "Failed to update project",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                className="min-h-[44px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="min-h-[44px]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              className="min-h-[100px] resize-y"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value: 'draft' | 'published') => 
                setFormData(prev => ({ ...prev, status: value }))
              }>
                <SelectTrigger className="min-h-[44px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2 pt-8">
              <Checkbox
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, featured: checked as boolean }))
                }
              />
              <Label htmlFor="featured">Featured Project</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle>Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((category) => (
              <div key={category.id} className="flex items-center space-x-2 min-h-[44px]">
                <Checkbox
                  id={`category-${category.id}`}
                  checked={selectedCategories.includes(category.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedCategories(prev => [...prev, category.id]);
                    } else {
                      setSelectedCategories(prev => prev.filter(id => id !== category.id));
                    }
                  }}
                  className="min-h-[20px] min-w-[20px]"
                />
                <Label htmlFor={`category-${category.id}`} className="text-sm flex-1 cursor-pointer">
                  {category.name}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Current Images with Reordering */}
      {images.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Current Images</CardTitle>
            <p className="text-sm text-muted-foreground">
              Use arrows to reorder, refresh icon to replace, or X to remove images.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {images.map((image, index) => (
                <Card key={image.id} className="relative">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="relative">
                        <img
                          src={image.image_url}
                          alt={image.alt_text}
                          className="w-full h-32 sm:h-40 object-cover rounded"
                        />
                        {replacingImageId === image.id && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded">
                            <Loader2 className="h-6 w-6 animate-spin text-white" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="min-h-[32px] min-w-[32px] p-0 touch-manipulation"
                            onClick={() => handleReplaceImage(image.id)}
                            disabled={replacingImageId === image.id}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="min-h-[32px] min-w-[32px] p-0 touch-manipulation"
                            onClick={() => removeExistingImage(image.id)}
                            disabled={replacingImageId === image.id}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="absolute top-2 left-2 flex flex-col space-y-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="min-h-[32px] min-w-[32px] p-0 touch-manipulation"
                            onClick={() => moveImageUp(index)}
                            disabled={index === 0 || replacingImageId === image.id}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="min-h-[32px] min-w-[32px] p-0 touch-manipulation"
                            onClick={() => moveImageDown(index)}
                            disabled={index === images.length - 1 || replacingImageId === image.id}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Input
                          placeholder="Alt text"
                          value={image.alt_text}
                          onChange={(e) => updateImageData(image.id, 'alt_text', e.target.value)}
                          disabled={replacingImageId === image.id}
                          className="min-h-[44px]"
                        />
                        <Input
                          placeholder="Caption"
                          value={image.caption || ''}
                          onChange={(e) => updateImageData(image.id, 'caption', e.target.value)}
                          disabled={replacingImageId === image.id}
                          className="min-h-[44px]"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add New Images */}
      <Card>
        <CardHeader>
          <CardTitle>Add New Images</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            type="file"
            multiple
            accept="image/*"
            onChange={handleNewImageUpload}
            className="min-h-[44px]"
          />

          {newImages.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {newImages.map((file, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`New image ${index + 1}`}
                    className="w-full h-24 sm:h-28 object-cover rounded"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="absolute top-1 right-1 min-h-[28px] min-w-[28px] p-0 touch-manipulation"
                    onClick={() => removeNewImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {file.name}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex justify-end">
        <Button type="submit" disabled={loading} className="min-h-[44px] w-full sm:w-auto">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Update Project
        </Button>
      </div>
    </form>
  );
};